import React, { useCallback, useEffect, useState } from "react";
import { useLocation, useNavigate } from "react-router-dom";
import { Bot, BriefcaseBusiness, HelpCircle, LayoutDashboard, ScrollText } from "lucide-react";

import { cn } from "../../lib/utils";
import { Can } from "../auth/components/Can";
import { Permission } from "../auth/logic/rbac";
import { isInternalReportsEnabled } from "../configs/logic/Slice";
import FeatureGate from "../featureFlags/components/FeatureGate";
import NavButton from "./NavButton";
import SidebarUserMenu from "./SidebarUserMenu";
import SidebarCollapseButton from "./SidebarCollapseButton";
import SidebarHeader from "./SidebarHeader";

type MainNavOption =
  | "DASHBOARD"
  | "CASES"
  | "AI_ASSISTANT"
  | "REPORTS"
  | "SETTINGS"
  | "USER_PROFILE"
  | "NONE";

const GlobalSidebar: React.FC = () => {
  const navigate = useNavigate();
  const location = useLocation();

  const getMainNavOptionFromPath = useCallback((): MainNavOption => {
    const pathname = location.pathname;

    if (pathname.startsWith("/cases")) return "CASES";
    if (pathname.startsWith("/dashboard")) return "DASHBOARD";
    if (pathname.startsWith("/assistant")) return "AI_ASSISTANT";
    if (pathname.startsWith("/internal-reports")) return "REPORTS";
    if (pathname.startsWith("/settings")) return "SETTINGS";
    if (pathname.startsWith("/user-profile")) return "USER_PROFILE";

    return "NONE";
  }, [location]);

  const getSavedSidebarState = () => {
    const saved = localStorage.getItem("sidebarCollapsed");
    return saved ? JSON.parse(saved) === true : false;
  };

  const [activeMainNav, setActiveMainNav] = useState<MainNavOption>(
    getMainNavOptionFromPath()
  );

  const [isCollapsed, setIsCollapsed] = useState<boolean>(
    getSavedSidebarState()
  );

  useEffect(() => {
    setActiveMainNav(getMainNavOptionFromPath());
  }, [location, getMainNavOptionFromPath]);

  useEffect(() => {
    localStorage.setItem("sidebarCollapsed", JSON.stringify(isCollapsed));
  }, [isCollapsed]);

  const toggleCollapse = () => {
    setIsCollapsed((prev) => !prev);
  };

  return (
    <div className="relative">
      <aside
        className={cn(
          "flex flex-col h-full bg-sidebar border text-sidebar-foreground",
          "transition-all duration-100 ease-in-out",
          isCollapsed ? "w-14" : "w-64"
        )}
        data-collapsed={isCollapsed}
      >
      <SidebarHeader isCollapsed={isCollapsed} />

        <nav className="flex-1 py-2 px-2 space-y-0.5 overflow-y-auto">
          <Can perform={Permission.ACCESS_DASHBOARD} variant="hide">
            <NavButton
              label="Dashboard"
              icon={LayoutDashboard}
              isActive={activeMainNav === "DASHBOARD"}
              onClick={() => navigate("/dashboard")}
              isCollapsed={isCollapsed}
            />
          </Can>

          <NavButton
            label="Cases"
            icon={BriefcaseBusiness}
            isActive={activeMainNav === "CASES"}
            onClick={() => navigate("/cases")}
            isCollapsed={isCollapsed}
          />

          <Can perform={Permission.ACCESS_INTERNAL_REPORTS} variant="hide">
            <FeatureGate selector={isInternalReportsEnabled}>
              <NavButton
                label="Reports"
                icon={ScrollText}
                isActive={activeMainNav === "REPORTS"}
                onClick={() => navigate("/internal-reports")}
                isCollapsed={isCollapsed}
              />
            </FeatureGate>
          </Can>

          <NavButton
            label="AI Assistant"
            icon={Bot}
            isActive={activeMainNav === "AI_ASSISTANT"}
            onClick={() => navigate("/assistant")}
            isCollapsed={isCollapsed}
          />
        </nav>

        <div className="flex flex-col px-2 pb-2 pt-0 gap-2 mt-auto bg-[#FAFAFA]">
          <div className={cn(isCollapsed ? "flex flex-col items-center space-y-2" : "")}>
            <NavButton
              label="Support"
              icon={HelpCircle}
              isActive={false}
              onClick={() => {
                window.open("https://support.canso.ai/support/tickets/new", "_blank");
              }}
              isCollapsed={isCollapsed}
            />
            <SidebarUserMenu isCollapsed={isCollapsed} />
          </div>
        </div>
      </aside>

      <SidebarCollapseButton
        isCollapsed={isCollapsed}
        toggleCollapse={toggleCollapse}
      />
    </div>
  );
};

export default GlobalSidebar;
